import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import axios from 'axios';
import {
    Box,
    Typography,
    Paper,
    IconButton,
    Fab,
    Drawer,
    List,
    ListItemButton,
    ListItemText,
    TextField,
    InputAdornment,
    Avatar,
    Stack,
    Fade,
    Badge,
    Button,
    Tooltip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import PersonIcon from '@mui/icons-material/Person';
import ImageIcon from '@mui/icons-material/Image';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { chatData } from './chatData';
import notificationSoundFile from '../sounds/notification.mp3';
import chatSendSoundFile from '../sounds/chatsend.mp3';

dayjs.extend(utc);
dayjs.extend(timezone);

// --- THEME CONSTANTS ---
const PRIMARY_COLOR = '#11224d';
const SECONDARY_COLOR = '#0e1d3a';
const ACCENT_COLOR = '#ffb300';
const BG_COLOR = '#f4f6f8';

const ChatFab = styled(Fab)(({ theme }) => ({
    position: 'fixed',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
    backgroundColor: PRIMARY_COLOR,
    color: ACCENT_COLOR,
    '&:hover': {
        backgroundColor: SECONDARY_COLOR,
    },
    zIndex: 1300,
    boxShadow: '0 4px 20px rgba(17, 34, 77, 0.4)',
}));

const ChatDrawer = styled(Drawer)({
    '& .MuiDrawer-paper': {
        height: '650px',
        maxHeight: '80vh',
        width: '400px',
        bottom: '20px',
        right: '20px',
        borderRadius: '16px',
        boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: `1px solid #e0e0e0`,
    },
});

const ChatHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    background: `linear-gradient(135deg, ${PRIMARY_COLOR}, ${SECONDARY_COLOR})`,
    color: 'white',
    flexShrink: 0,
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
}));

const MessageContainer = styled(Box)({
    flexGrow: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '20px',
    backgroundColor: BG_COLOR,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
});

const MessageBubble = styled(Paper)(({ theme, sender }) => ({
    padding: '12px 16px',
    borderRadius: sender === 'user' ? '16px 16px 2px 16px' : '16px 16px 16px 2px',
    maxWidth: '85%',
    wordWrap: 'break-word',
    alignSelf: sender === 'user' ? 'flex-end' : 'flex-start',
    backgroundColor: sender === 'user' ? PRIMARY_COLOR : '#ffffff',
    color: sender === 'user' ? '#ffffff' : '#1a1a1a',
    boxShadow: sender === 'user' ? '0 4px 12px rgba(17, 34, 77, 0.2)' : '0 2px 8px rgba(0,0,0,0.05)',
    position: 'relative',
    fontSize: '0.9rem',
    lineHeight: 1.5,
}));

const QuickOptionButton = styled(ListItemButton)(({ theme }) => ({
    borderRadius: '20px',
    marginBottom: '8px',
    backgroundColor: '#ffffff',
    border: '1px solid #eef0f2',
    padding: '8px 16px',
    transition: 'all 0.2s',
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
    '&:hover': {
        backgroundColor: '#fff8e1',
        borderColor: ACCENT_COLOR,
        transform: 'translateX(4px)',
    },
}));

const ChatInputContainer = styled(Box)({
    padding: '16px',
    borderTop: '1px solid #f0f0f0',
    backgroundColor: '#fff',
    flexShrink: 0,
});

const ImagePreview = styled(Box)({
    position: 'relative',
    display: 'inline-block',
    marginBottom: '8px',
    '& img': {
        maxWidth: '150px',
        maxHeight: '150px',
        borderRadius: '8px',
        border: '2px solid #e0e0e0',
    }
});

const GREETINGS = [
    "Hi! 👋 How can I assist you with ShipperDock today?",
    "Hi there! 👋 Welcome to ShipperDock. How can I help you?",
    "Greetings! 👋 I'm here to help with your ShipperDock experience. What do you need?",
    "Hi! 👋 ShipperDock Support here. How can I be of service?"
];

const levenshteinDistance = (a, b) => {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;
    const matrix = [];
    for (let i = 0; i <= b.length; i++) matrix[i] = [i];
    for (let j = 0; j <= a.length; j++) matrix[0][j] = j;
    for (let i = 1; i <= b.length; i++) {
        for (let j = 1; j <= a.length; j++) {
            if (b.charAt(i - 1) === a.charAt(j - 1)) {
                matrix[i][j] = matrix[i - 1][j - 1];
            } else {
                matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, Math.min(matrix[i][j - 1] + 1, matrix[i - 1][j] + 1));
            }
        }
    }
    return matrix[b.length][a.length];
};

const TypingIndicator = () => (
    <Box sx={{ display: 'flex', gap: 0.5, p: 1, alignItems: 'center' }}>
        <Box sx={{ width: 6, height: 6, bgcolor: '#999', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out both', animationDelay: '-0.32s' }} />
        <Box sx={{ width: 6, height: 6, bgcolor: '#999', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out both', animationDelay: '-0.16s' }} />
        <Box sx={{ width: 6, height: 6, bgcolor: '#999', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out both' }} />
        <style>{`
            @keyframes bounce {
                0%, 80%, 100% { transform: scale(0); }
                40% { transform: scale(1); }
            }
        `}</style>
    </Box>
);

const LiveChat = ({ customTrigger }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [chatState, setChatState] = useState('categories');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [chatSession, setChatSession] = useState(null);
    const [isTyping, setIsTyping] = useState(false);
    const [confusionCount, setConfusionCount] = useState(0);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const lastMessageIdRef = useRef(0);
    const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';
    const messagesEndRef = useRef(null);
    const notificationAudioRef = useRef(null);
    const sendAudioRef = useRef(null);
    const responseTimeoutRef = useRef(null);
    const fileInputRef = useRef(null);
    const textInputRef = useRef(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const messagesRef = useRef(messages);
    const [endChatDialogOpen, setEndChatDialogOpen] = useState(false);
    const prevUnreadCountRef = useRef(0);
    const isFirstUnreadCheck = useRef(true);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        notificationAudioRef.current = new Audio(notificationSoundFile);
        sendAudioRef.current = new Audio(chatSendSoundFile);
    }, []);

    useEffect(() => {
        return () => {
            if (responseTimeoutRef.current) clearTimeout(responseTimeoutRef.current);
        };
    }, []);

    useEffect(scrollToBottom, [messages, isTyping]);

    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    useEffect(() => {
        if (isFirstUnreadCheck.current) {
            isFirstUnreadCheck.current = false;
            prevUnreadCountRef.current = unreadCount;
            return;
        }

        if (unreadCount > prevUnreadCountRef.current) {
            if (chatState !== 'live_chat') {
                 notificationAudioRef.current?.play().catch(e => console.log(e));
            }
        }
        prevUnreadCountRef.current = unreadCount;
    }, [unreadCount, chatState]);

    useEffect(() => {
        if (isOpen && chatSession && unreadCount > 0) {
            const token = localStorage.getItem('jwtToken');
            axios.put(`${API_BASE_URL}/api/chat/${chatSession.id}/read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            }).then(() => setUnreadCount(0)).catch(e => console.error(e));
        }
    }, [isOpen, chatSession, unreadCount]);

    // Handle paste event for images
    useEffect(() => {
        const handlePaste = (e) => {
            if (!isOpen || (chatState !== 'live_chat' && chatState !== 'questions')) return;
            
            const items = e.clipboardData?.items;
            if (!items) return;

            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    e.preventDefault();
                    const file = items[i].getAsFile();
                    handleImageSelection(file);
                    break;
                }
            }
        };

        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [isOpen, chatState]);

    const handleImageSelection = (file) => {
    if (file && file.type.startsWith('image/')) {

        // ✅ Increased size limit to 10MB
        if (file.size > 10 * 1024 * 1024) {
            alert('Image size must be less than 10MB');
            return;
        }

        setSelectedImage(file);

        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
    }
};


    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            handleImageSelection(file);
        }
    };

    const clearImageSelection = () => {
        setSelectedImage(null);
        setImagePreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const restoreChatSession = async (chat) => {
        setChatState('live_chat');
        try {
            const token = localStorage.getItem('jwtToken');
            const msgRes = await axios.get(`${API_BASE_URL}/api/chat/${chat.id}/messages`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const historyMessages = msgRes.data.map(m => ({
                sender: m.sender === 'admin' ? 'admin' : 'user',
                text: m.message,
                id: m.id,
                timestamp: new Date(m.created_at),
                type: m.message_type || 'text',
                imageUrl: m.image_url
            }));

            setMessages(historyMessages);
            
            // Update lastMessageIdRef to the latest message
            if (msgRes.data.length > 0) {
                lastMessageIdRef.current = msgRes.data[msgRes.data.length - 1].id;
            }

            if (historyMessages.length > 0) {
                const lastMsg = historyMessages[historyMessages.length - 1];
                if (lastMsg.sender === 'user') {
                    setMessages(prev => [...prev, {
                        sender: 'bot',
                        text: "We have received your message. An admin will be with you shortly.",
                        id: 'waiting-msg-' + Date.now(),
                        timestamp: new Date(),
                        type: 'text'
                    }]);
                }
            }
        } catch (err) {
            console.error("Error restoring session", err);
        }
    };

    const addMessage = (sender, text, options = [], type = 'text', timestamp = new Date(), imageUrl = null) => {
        const newMessage = { 
            sender, 
            text, 
            options, 
            id: Date.now() + Math.random(),
            timestamp: new Date(timestamp),
            type,
            imageUrl
        };
        setMessages(prev => [...prev, newMessage]);
        
        if ((isOpen && sender === 'bot') || sender === 'admin') {
            notificationAudioRef.current?.play().catch(e => console.log('Audio play failed', e));
        }
    };

    const handleReset = (showIntro = true) => {
        if (showIntro) {
            setMessages([]);
            setIsTyping(true);
            setTimeout(() => {
                setIsTyping(false);
                const userRole = localStorage.getItem('role');
                
                const path = window.location.pathname;
                let contextMsg = "";
                let availableCategories = chatData
                    .filter(c => !c.roles || c.roles.includes(userRole))
                    .map(c => c.category);

                if (path.includes('post-loads') && availableCategories.includes('Posting & Managing Loads (Shippers)')) {
                    contextMsg = "I see you're on the Post Loads page.";
                    availableCategories = ['Posting & Managing Loads (Shippers)', ...availableCategories.filter(c => c !== 'Posting & Managing Loads (Shippers)')];
                } else if (path.includes('search') && availableCategories.includes('Finding & Booking Loads (Carriers)')) {
                    contextMsg = "I see you're searching for loads.";
                    availableCategories = ['Finding & Booking Loads (Carriers)', ...availableCategories.filter(c => c !== 'Finding & Booking Loads (Carriers)')];
                }

                const hour = new Date().getHours();
                let timeGreeting = hour < 12 ? "Good morning! ☀️" : hour < 18 ? "Good afternoon! 🌤️" : "Good evening! 🌙";
                
                const finalGreeting = `${timeGreeting} ${contextMsg} How can I assist you with ShipperDock today?`;
                addMessage('bot', finalGreeting, [...availableCategories, 'Talk to Support']);
            }, 800);
        }
        setChatState('categories');
        setSelectedCategory(null);
        setConfusionCount(0);
        setChatSession(null);
        clearImageSelection();
        lastMessageIdRef.current = 0;
    };

    useEffect(() => {
        const checkActiveSession = async () => {
            try {
                const token = localStorage.getItem('jwtToken');
                if (!token) return;

                const res = await axios.get(`${API_BASE_URL}/api/chat/user/active`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                const activeChat = res.data;

                if (activeChat) {
                    setChatSession(activeChat);
                    setUnreadCount(activeChat.unread_count || 0);

                    if (isOpen) {
                        if (chatState !== 'live_chat') {
                            const lastUpdate = dayjs(activeChat.updated_at);
                            if (dayjs().diff(lastUpdate, 'hour') >= 24) {
                                setEndChatDialogOpen(true);
                            } else {
                                restoreChatSession(activeChat);
                            }
                        }
                    }
                } else if (isOpen && messagesRef.current.length === 0 && chatState !== 'live_chat') {
                    handleReset();
                }
            } catch (err) {
                console.error("Error checking active session", err);
            }
        };

        checkActiveSession();
        const interval = setInterval(checkActiveSession, 10000);
        return () => clearInterval(interval);
    }, [isOpen, chatState]);

    const handleNewChat = () => {
        if (chatSession) {
            const token = localStorage.getItem('jwtToken');
            axios.patch(`${API_BASE_URL}/api/chat/${chatSession.id}/close`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            }).catch(e => console.error(e));
        }
        handleReset(true);
    };

    const handleContinueSession = () => {
        setEndChatDialogOpen(false);
        if (chatSession) {
            restoreChatSession(chatSession);
        }
    };

    const handleStartNewSession = async () => {
        if (chatSession) {
            try {
                const token = localStorage.getItem('jwtToken');
                await axios.patch(`${API_BASE_URL}/api/chat/${chatSession.id}/close`, {}, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } catch (err) {
                console.error("Failed to close chat session", err);
            }
        }
        setEndChatDialogOpen(false);
        handleReset(true);
    };

    const findBestMatch = (text) => {
        const lowerText = text.toLowerCase();
        const userTokens = lowerText.replace(/[.,/#!$%^&*;:{}=\-_`~()]/g,"").split(/\s+/).filter(t => t.length > 2);
        const userRole = localStorage.getItem('role');
        let bestMatch = { score: 0, question: null };

        for (const cat of chatData) {
            if (cat.roles && !cat.roles.includes(userRole)) continue;

            for (const q of cat.questions) {
                const keywords = q.keywords || [];
                let currentScore = 0;

                if (lowerText.includes(q.q.toLowerCase())) {
                    currentScore += 50;
                }

                for (const keyword of keywords) {
                    const lowerKeyword = keyword.toLowerCase();
                    
                    if (lowerText.includes(lowerKeyword)) {
                        currentScore += 5;
                        continue;
                    }

                    for (const token of userTokens) {
                        const dist = levenshteinDistance(token, lowerKeyword);
                        const allowedErrors = lowerKeyword.length > 5 ? 2 : (lowerKeyword.length > 3 ? 1 : 0);
                        
                        if (dist <= allowedErrors) {
                            currentScore += 3;
                            break;
                        }
                    }
                }

                if (currentScore > bestMatch.score) {
                    bestMatch = { score: currentScore, question: q };
                }
            }
        }
        return bestMatch.score >= 3 ? bestMatch.question : null;
    };

    const handleOptionClick = (optionText) => {
        setConfusionCount(0);

        if (optionText === 'Talk to Support') {
            handleStartLiveChat(true);
            return;
        }

        const category = chatData.find(c => c.category === optionText);
        if (category) {
            addMessage('user', optionText);
            setSelectedCategory(category);
            setChatState('questions');
            setIsTyping(true);
            setTimeout(() => {
                setIsTyping(false);
                addMessage('bot', `Here are some common questions about ${category.category}:`, category.questions.map(q => q.q));
            }, 600);
            return;
        }

        let question = selectedCategory?.questions.find(q => q.q === optionText);
        
        if (!question) {
            const catWithQuestion = chatData.find(c => c.questions.some(q => q.q === optionText));
            if (catWithQuestion) {
                question = catWithQuestion.questions.find(q => q.q === optionText);
            }
        }

        if (question) {
            addMessage('user', optionText);
            setIsTyping(true);
            setTimeout(() => {
                setIsTyping(false);
                addMessage('bot', question.a);
                if (question.escalate) {
                    setTimeout(() => handleStartLiveChat(), 1500);
                }
            }, 800);
        }
    };

    const handleStartLiveChat = async (fromUser = false) => {
        if (fromUser) {
            addMessage('user', 'Talk to Support');
        }
        setChatState('live_chat');
        
        try {
            const token = localStorage.getItem('jwtToken');
            const userStr = localStorage.getItem('user');
            const role = localStorage.getItem('role');
            
            if (!token || !userStr) {
                setIsTyping(true);
                setTimeout(() => {
                    setIsTyping(false);
                    addMessage('bot', 'Please log in to chat with a support agent.');
                }, 600);
                return;
            }
            const user = JSON.parse(userStr);

            setIsTyping(true);
            await new Promise(resolve => setTimeout(resolve, 1000));
            setIsTyping(false);
            addMessage('bot', "You've been connected to our support team. Please describe your issue, and an agent will be with you shortly.");

            const res = await axios.post(`${API_BASE_URL}/api/chat/create`, 
                { userId: user.id, role: role, subject: 'Live Support Request' },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            let newChatSession = res.data;

            if (dayjs(newChatSession.updated_at).isBefore(dayjs().subtract(24, 'hour'))) {
                try {
                    await axios.patch(`${API_BASE_URL}/api/chat/${newChatSession.id}/close`, {}, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    const retryRes = await axios.post(`${API_BASE_URL}/api/chat/create`, 
                        { userId: user.id, role: role, subject: 'Live Support Request' },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    newChatSession = retryRes.data;
                } catch (e) {
                    console.error("Error refreshing stale session", e);
                }
            }
            setChatSession(newChatSession);
            lastMessageIdRef.current = 0;
        } catch (err) {
            console.error(err);
            setIsTyping(false);
            addMessage('bot', 'Unable to connect to support server. Please try again later.');
        }
    };

    useEffect(() => {
        if (chatState !== 'live_chat' || !chatSession) return;

        const pollMessages = async () => {
            if (document.hidden) return;

            try {
                const token = localStorage.getItem('jwtToken');
                const res = await axios.get(`${API_BASE_URL}/api/chat/${chatSession.id}/messages`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                
                const dbMessages = res.data;
                const lastId = lastMessageIdRef.current;

                const newMessages = dbMessages.filter(msg => msg.id > lastId);

                if (newMessages.length > 0) {
                    setMessages(prev => {
                        // Remove temporary messages
                        const withoutTemp = prev.filter(m => !String(m.id).startsWith('temp-'));
                        
                        // Create a map of existing real message IDs
                        const existingIds = new Set(withoutTemp.map(m => m.id));
                        
                        // Add only truly new messages
                        const uniqueNewMessages = newMessages.map(msg => ({
                            sender: msg.sender === 'admin' ? 'admin' : 'user',
                            text: msg.message,
                            id: msg.id,
                            timestamp: new Date(msg.created_at),
                            type: msg.message_type || 'text',
                            imageUrl: msg.image_url
                        })).filter(m => !existingIds.has(m.id));
                        
                        // Play notification for admin messages
                        newMessages.forEach(msg => {
                            if (msg.sender === 'admin') {
                                if (responseTimeoutRef.current) {
                                    clearTimeout(responseTimeoutRef.current);
                                    responseTimeoutRef.current = null;
                                }
                                if (notificationAudioRef.current) {
                                    notificationAudioRef.current.play().catch(e => console.log('Audio play failed', e));
                                }
                            }
                        });
                        
                        return [...withoutTemp, ...uniqueNewMessages];
                    });
                    
                    lastMessageIdRef.current = dbMessages[dbMessages.length - 1].id;
                }
            } catch (err) {
                console.error(err);
            }
        };

        const interval = setInterval(pollMessages, 3000);
        return () => clearInterval(interval);
    }, [chatState, chatSession]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        const input = textInputRef.current;
        const messageText = input.value.trim();
        
        // Check if we have either text or image
        if (!messageText && !selectedImage) return;

        if (chatState === 'live_chat' && chatSession) {
            try {
                const token = localStorage.getItem('jwtToken');

                if (selectedImage) {
                    // Send image
                    const reader = new FileReader();
                    reader.onloadend = async () => {
                        const base64Image = reader.result;
                        
                        // Generate temporary ID for optimistic update
                        const tempId = 'temp-' + Date.now();
                        
                        // Add message to UI immediately
                        const tempMessage = {
                            sender: 'user',
                            text: messageText || '',
                            id: tempId,
                            timestamp: new Date(),
                            type: 'image',
                            imageUrl: base64Image
                        };
                        setMessages(prev => [...prev, tempMessage]);
                        
                        sendAudioRef.current?.play().catch(e => console.log('Audio play failed', e));

                        await axios.post(`${API_BASE_URL}/api/chat/${chatSession.id}/message`, 
                            { 
                                sender: 'user', 
                                text: messageText || 'Sent an image',
                                messageType: 'image',
                                imageData: base64Image
                            },
                            { headers: { Authorization: `Bearer ${token}` } }
                        );

                        clearImageSelection();
                        input.value = '';
                    };
                    reader.readAsDataURL(selectedImage);
                } else {
                    // Generate temporary ID for optimistic update
                    const tempId = 'temp-' + Date.now();
                    
                    // Add message to UI immediately
                    const tempMessage = {
                        sender: 'user',
                        text: messageText,
                        id: tempId,
                        timestamp: new Date(),
                        type: 'text'
                    };
                    setMessages(prev => [...prev, tempMessage]);
                    
                    sendAudioRef.current?.play().catch(e => console.log('Audio play failed', e));
                    input.value = '';

                    await axios.post(`${API_BASE_URL}/api/chat/${chatSession.id}/message`, 
                        { sender: 'user', text: messageText },
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                }

                if (!responseTimeoutRef.current) {
                    responseTimeoutRef.current = setTimeout(() => {
                        addMessage('bot', "Our agents are assisting other customers at the moment. An agent will be with you shortly. Thank you for your patience.");
                        if (notificationAudioRef.current) {
                            notificationAudioRef.current.play().catch(e => console.log(e));
                        }
                        responseTimeoutRef.current = null;
                    }, 120000);
                }
            } catch (err) {
                console.error("Failed to send", err);
            }
        } else {
            // Bot conversation - images not supported here
            if (selectedImage) {
                addMessage('bot', "Image sharing is only available in live chat. Would you like to talk to support?", ['Talk to Support']);
                clearImageSelection();
                return;
            }

            addMessage('user', messageText);
            sendAudioRef.current?.play().catch(e => console.log('Audio play failed', e));
            input.value = '';

            const negativeKeywords = ['scam', 'fraud', 'broken', 'terrible', 'horrible', 'useless', 'stupid', 'hate', 'angry'];
            if (negativeKeywords.some(kw => messageText.toLowerCase().includes(kw))) {
                setIsTyping(true);
                setTimeout(() => {
                    setIsTyping(false);
                    addMessage('bot', "I'm sorry to hear you're having trouble. I'm connecting you to a live support agent immediately to resolve this.");
                    handleStartLiveChat();
                }, 1000);
                return;
            }

            setIsTyping(true);
            setTimeout(() => {
                setIsTyping(false);
                const match = findBestMatch(messageText);
                if (match) {
                    setConfusionCount(0);
                    addMessage('bot', match.a);
                    if (match.escalate) {
                        setTimeout(() => handleStartLiveChat(), 1500);
                    }
                } else {
                    const lowerText = messageText.toLowerCase();
                    const greetingRegex = /\b(hi|hello|hey|yo|greetings|hlo|hy|heya|howdy|sup)\b/;
                    const timeGreetingRegex = /\b(good\s(morn|morning|mrng|afternoon|evening|eve))\b/;
                    const welcomeRegex = /\b(welcome|welcom|elcome|wlcm)\b/;

                    if ((greetingRegex.test(lowerText) || timeGreetingRegex.test(lowerText) || welcomeRegex.test(lowerText)) && lowerText.length < 50) {
                        const randomGreeting = GREETINGS[Math.floor(Math.random() * GREETINGS.length)];
                        addMessage('bot', randomGreeting);
                        return;
                    }

                    if (/\b(thanks|thank|thx|thnks|tks|tnx|appreciate|gracias|ty)\b/.test(lowerText)) {
                        addMessage('bot', "You're welcome! Let me know if you need anything else.");
                        return;
                    }

                    if (/\b(bye|goodbye|see ya|cya|later|bbye)\b/.test(lowerText)) {
                        addMessage('bot', "Goodbye! Have a great day! 👋");
                        return;
                    }

                    const newConfusionCount = confusionCount + 1;
                    setConfusionCount(newConfusionCount);

                    if (newConfusionCount >= 2) {
                        addMessage('bot', "I'm having trouble understanding. Let me connect you with a support agent who can help.");
                        setTimeout(() => handleStartLiveChat(), 1500);
                    } else {
                        const userRole = localStorage.getItem('role');
                        const availableCategories = chatData
                            .filter(c => !c.roles || c.roles.includes(userRole))
                            .map(c => c.category);
                        addMessage('bot', "I'm not sure I understand. Here are some topics I can help with, or you can ask to talk to support.", [...availableCategories, 'Talk to Support']);
                    }
                }
            }, 1000);
        }
    };

    return (
        <>
            {customTrigger ? (
                React.cloneElement(customTrigger, { 
                    onClick: () => setIsOpen(true) 
                })
            ) : (
                <ChatFab onClick={() => setIsOpen(true)}>
                    <Badge badgeContent={unreadCount} color="error">
                        <ChatIcon sx={{ fontSize: 28 }} />
                    </Badge>
                </ChatFab>
            )}
            <ChatDrawer
                anchor="right"
                open={isOpen}
                onClose={() => setIsOpen(false)}
                variant="temporary"
            >
                <ChatHeader>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        {chatState !== 'categories' && (
                            <IconButton onClick={handleReset} size="small" sx={{ color: 'white' }}>
                                <ArrowBackIcon />
                            </IconButton>
                        )}
                        <Avatar sx={{ width: 36, height: 36, bgcolor: 'white', color: PRIMARY_COLOR }}>
                            {chatState === 'live_chat' ? <SupportAgentIcon /> : <AutoAwesomeIcon />}
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>ShipperDock Support</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                {chatState === 'live_chat' ? 'Live Agent' : chatState === 'feedback' ? 'Feedback' : 'Automated Assistant'}
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack direction="row" alignItems="center">
                        <Tooltip title="Start New Chat">
                            <IconButton onClick={handleNewChat} size="small" sx={{ color: 'white', mr: 1 }}>
                                <AddIcon />
                            </IconButton>
                        </Tooltip>
                        <IconButton onClick={() => setIsOpen(false)} sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </ChatHeader>

                <MessageContainer>
                    <Stack spacing={2}>
                        {messages.map((msg) => (
                            <Fade in={true} key={msg.id}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                                    maxWidth: '100%'
                                }}>
                                    <Box sx={{ display: 'flex', gap: 1, flexDirection: msg.sender === 'user' ? 'row-reverse' : 'row', maxWidth: '100%' }}>
                                        {msg.sender !== 'user' && (
                                            <Avatar sx={{ width: 28, height: 28, bgcolor: msg.sender === 'admin' ? ACCENT_COLOR : '#e0e0e0', color: PRIMARY_COLOR }}>
                                                {msg.sender === 'admin' ? <PersonIcon fontSize="small" /> : <AutoAwesomeIcon fontSize="small" />}
                                            </Avatar>
                                        )}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '85%' }}>
                                            {msg.type === 'image' && msg.imageUrl && (
                                                <Box sx={{ mb: 0.5 }}>
                                                    <img 
                                                        src={msg.imageUrl.startsWith('data:') || msg.imageUrl.startsWith('http') ? msg.imageUrl : `${API_BASE_URL}${msg.imageUrl}`} 
                                                        alt="Shared" 
                                                        style={{ 
                                                            maxWidth: '250px', 
                                                            maxHeight: '250px', 
                                                            borderRadius: '12px',
                                                            cursor: 'pointer',
                                                            border: '2px solid #e0e0e0'
                                                        }}
                                                        onClick={() => window.open(msg.imageUrl.startsWith('data:') || msg.imageUrl.startsWith('http') ? msg.imageUrl : `${API_BASE_URL}${msg.imageUrl}`, '_blank')}
                                                    />
                                                </Box>
                                            )}
                                            {msg.text && (
                                                <MessageBubble sender={msg.sender} elevation={0}>
                                                    <Typography variant="body2">{msg.text}</Typography>
                                                </MessageBubble>
                                            )}
                                        </Box>
                                    </Box>

                                    <Typography variant="caption" sx={{ color: '#999', mt: 0.5, mx: 1, fontSize: '0.65rem' }}>
                                        {dayjs.utc(msg.timestamp).local().format('h:mm A')}
                                    </Typography>

                                    {msg.options && msg.options.length > 0 && (
                                        <List sx={{ mt: 0.5, width: '100%', pl: msg.sender === 'user' ? 0 : 5 }}>
                                            {msg.options.map((option) => (
                                                <QuickOptionButton key={option} onClick={() => handleOptionClick(option)}>
                                                    <ListItemText primary={option} primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }} />
                                                </QuickOptionButton>
                                            ))}
                                        </List>
                                    )}
                                </Box>
                            </Fade>
                        ))}
                        {isTyping && (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Avatar sx={{ width: 28, height: 28, bgcolor: '#e0e0e0', color: PRIMARY_COLOR }}>
                                    <AutoAwesomeIcon fontSize="small" />
                                </Avatar>
                                <Paper elevation={0} sx={{ p: 1, borderRadius: '16px 16px 16px 2px', bgcolor: '#fff' }}>
                                    <TypingIndicator />
                                </Paper>
                            </Box>
                        )}
                    </Stack>
                    <div ref={messagesEndRef} />
                </MessageContainer>

                <ChatInputContainer>
                    {imagePreview && (
                        <ImagePreview>
                            <img src={imagePreview} alt="Preview" />
                            <IconButton 
                                size="small" 
                                onClick={clearImageSelection}
                                sx={{ 
                                    position: 'absolute', 
                                    top: -8, 
                                    right: -8, 
                                    bgcolor: 'white',
                                    boxShadow: 2,
                                    '&:hover': { bgcolor: '#f5f5f5' }
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </ImagePreview>
                    )}
                    <Box component="form" onSubmit={handleSendMessage} sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                        <input
                            type="file"
                            ref={fileInputRef}
                            accept="image/*"
                            onChange={handleFileSelect}
                            style={{ display: 'none' }}
                        />
                        {chatState === 'live_chat' && (
                            <Tooltip title="Attach image (or paste with Ctrl+V)">
                                <IconButton 
                                    size="small"
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{ color: PRIMARY_COLOR }}
                                >
                                    <ImageIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                        <TextField
                            fullWidth
                            size="small"
                            inputRef={textInputRef}
                            placeholder={chatState === 'live_chat' ? "Type a message or paste an image..." : "Ask a question..."}
                            autoComplete="off"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '24px',
                                    backgroundColor: '#f9f9f9',
                                    '& fieldset': { borderColor: 'transparent' },
                                    '&:hover fieldset': { borderColor: '#e0e0e0' },
                                    '&.Mui-focused fieldset': { borderColor: PRIMARY_COLOR },
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton type="submit" sx={{ color: PRIMARY_COLOR }} edge="end">
                                            <SendIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    {chatState === 'live_chat' && (
                        <Typography variant="caption" sx={{ color: '#999', mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                            💡 Tip: Press Ctrl+V to paste images directly
                        </Typography>
                    )}
                </ChatInputContainer>
            </ChatDrawer>

            <Dialog 
                open={endChatDialogOpen} 
                onClose={() => {}}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        padding: 1
                    }
                }}
            >
                <DialogTitle sx={{ color: PRIMARY_COLOR, fontWeight: 'bold' }}>
                    Previous Conversation Found
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#555' }}>
                        It looks like you had a conversation more than 24 hours ago. Would you like to continue that chat or start a new inquiry?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 2, gap: 1 }}>
                    <Button 
                        onClick={handleContinueSession} 
                        variant="outlined"
                        sx={{ color: PRIMARY_COLOR, borderColor: PRIMARY_COLOR, borderRadius: '8px', textTransform: 'none' }}
                    >
                        Continue Chat
                    </Button>
                    <Button 
                        onClick={handleStartNewSession} 
                        variant="contained"
                        sx={{ bgcolor: PRIMARY_COLOR, color: 'white', borderRadius: '8px', textTransform: 'none', '&:hover': { bgcolor: SECONDARY_COLOR } }}
                    >
                        Start New Chat
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default LiveChat;